// src/AddStudent.js
import React, { useState, useEffect, useMemo } from 'react';
import API from './api';
import { toast } from 'react-toastify';
import { FaUserPlus, FaSave, FaTimes, FaEdit } from 'react-icons/fa';

import StudentTaxonomyEditor from './StudentTaxonomyEditor';
import { saveStudentTaxonomy } from './api';
import SuggestMatchesPanel from './SuggestMatchesPanel';

const today = new Date().toISOString().split('T')[0];
const emptyParent = {
  id: undefined,
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  relation: 'mother',
};

const requiredFields = [
  'first_name',
  'last_name',
  'dob',
  'gender',
  'default_teacher_id',
  'address_street',
  'address_city',
  'address_state',
  'address_zip',
  'address_country',
  'uci',
];

const teacherHasProgram = (teacherTaxo, programCode) => {
  if (!teacherTaxo || !Array.isArray(teacherTaxo.strengths)) return false;
  return teacherTaxo.strengths.some(
    (s) => (s.code || '').toUpperCase() === programCode.toUpperCase()
  );
};

// Fallback labeli za UI (ako taxonomy nema ili je nepotpun)
const STATIC_DISCIPLINE_LABELS = {
  EI: 'Early Intervention (EI)',
  PI: 'Project Impact (PI)',
  ABA: 'Applied Behavior Analysis (ABA)',
  OT: 'Occupational Therapy (OT)',
  PT: 'Physical Therapy (PT)',
  ST: 'Speech Therapy (ST)',
};

// Svi poznati tipovi disciplina na FE
const ALL_DISCIPLINE_CODES = Object.keys(STATIC_DISCIPLINE_LABELS);

// Inicijalna selekcija disciplina (dinamički po ALL_DISCIPLINE_CODES)
const initialDisciplineSelection = ALL_DISCIPLINE_CODES.reduce((acc, code) => {
  acc[code] = false;
  return acc;
}, {});

// Prioritet za automatski izbor primarne discipline kod novog studenta
const DISCIPLINE_PRIORITY_ORDER = ['EI', 'PI', 'ABA', 'OT', 'PT', 'ST'];

const AddStudent = ({ student, onSaved, onCancel }) => {
  const [needs, setNeeds] = useState({ needs: [] });
  const [needsDirty, setNeedsDirty] = useState(false);

  const [teachers, setTeachers] = useState([]);
  const [teacherTaxoMap, setTeacherTaxoMap] = useState(new Map()); // id -> { strengths, limitations }
  const [taxoLoaded, setTaxoLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const [studentData, setStudentData] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    is_active: true,
    default_teacher_id: '',
    latitude: '',
    longitude: '',
    notes: '',
    uci: '',
    program_type: 'EI', // čuvamo radi kompatibilnosti (EI/PI)
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    address_country: '',
    alt_address_street: '',
    alt_address_city: '',
    alt_address_state: '',
    alt_address_zip: '',
    alt_address_country: '',
    service_start_date: today,
    service_end_date: today,
    intake_goals: '',
    additional_notes: '',
    parent_concerns: '',
    referring_agent: '',
    referring_agent_phone: '',
    referring_agent_unit: '',
    referring_agent_unit_id: '',
    onboarding_documents: 'not signed',
    provider_gender_preference: 'any',
    telehealth_ok: false,
    parents: [{ ...emptyParent }],
  });

  const [errors, setErrors] = useState({});
  const [teacherHistory, setTeacherHistory] = useState([]);

  // DISCIPLINE state
  const [disciplineSelection, setDisciplineSelection] = useState(
    initialDisciplineSelection
  );
  const [primaryServiceType, setPrimaryServiceType] = useState(''); // EI / PI / ABA / OT / PT

  // Koje discipline su uopšte dozvoljene za ovu organizaciju (ili globalno)
  const [availableServiceTypes, setAvailableServiceTypes] = useState(
    () => new Set()
  );
  const [disciplinesLoaded, setDisciplinesLoaded] = useState(false);

  // Discipline taxonomy (category='discipline', codes PROG_EI / PROG_PI / ...)
  const [programTaxonomy, setProgramTaxonomy] = useState([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);

  const isEditMode = !!(student && student.id);

  // --- Učitaj discipline taxonomy (category='discipline', kodovi PROG_*) ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // FE sada koristi /taxonomy/disciplines, a u DB je category.slug = 'discipline'
        const res = await API.get('/taxonomy/disciplines');
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setProgramTaxonomy(rows);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load discipline taxonomy:', err);
          setProgramTaxonomy([]);
          toast.error('Could not load discipline taxonomy (disciplines).');
        }
      } finally {
        if (!cancelled) setProgramsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mapiranje discipline (EI/PI/...) → PROG_* kod iz taxonomy
  const programCodeMap = useMemo(() => {
    const map = {};
    programTaxonomy.forEach((item) => {
      const code = (item.code || '').toUpperCase(); // PROG_EI
      const m = code.match(/^PROG_(.+)$/);
      if (!m) return;
      const disciplineCode = m[1].toUpperCase(); // EI
      if (ALL_DISCIPLINE_CODES.includes(disciplineCode)) {
        map[disciplineCode] = code;
      }
    });
    return map;
  }, [programTaxonomy]);

  // Labeli za UI iz taxonomy, sa fallback-om na statičke
  const programLabelMap = useMemo(() => {
    const map = {};
    programTaxonomy.forEach((item) => {
      const code = (item.code || '').toUpperCase(); // PROG_EI
      const m = code.match(/^PROG_(.+)$/);
      if (!m) return;
      const disciplineCode = m[1].toUpperCase(); // EI
      const label =
        item.label || STATIC_DISCIPLINE_LABELS[disciplineCode] || code;
      map[disciplineCode] = label;
    });
    return map;
  }, [programTaxonomy]);

  // --- Učitaj dostupne discipline za ovu organizaciju (sa globalnim fallback-om u BE) ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/disciplines/service-types');
        let list = res.data || [];
        if (!Array.isArray(list)) list = [];

        const codes = new Set();
        list.forEach((item) => {
          const raw =
            typeof item === 'string'
              ? item
              : item.service_type || item.code || item.serviceType || '';
          const code = (raw || '').toUpperCase();
          // prihvatamo samo one koje FE poznaje (ALL_DISCIPLINE_CODES / initialDisciplineSelection)
          if (
            code &&
            Object.prototype.hasOwnProperty.call(initialDisciplineSelection, code)
          ) {
            codes.add(code);
          }
        });

        if (!cancelled) {
          setAvailableServiceTypes(codes);
          setDisciplinesLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          setAvailableServiceTypes(new Set());
          setDisciplinesLoaded(true);
          toast.error(
            'Could not load disciplines for this organization. Please check organization setup.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Sinhronizacija program_type (samo EI/PI) iz primarne discipline
  const syncProgramTypeFromPrimary = (primary) => {
    const next = primary === 'PI' ? 'PI' : 'EI';
    setStudentData((prev) => ({ ...prev, program_type: next }));
  };

  // Uključivanje/isključivanje discipline (samo ako je dostupna)
  const toggleServiceType = (type) => {
    setDisciplineSelection((prev) => {
      const next = { ...prev, [type]: !prev[type] };

      let nextPrimary = primaryServiceType;
      if (!prev[type] && !primaryServiceType) {
        // uključujemo novu disciplinu, a nema primarne → ova postaje primary
        nextPrimary = type;
      }
      if (prev[type] && primaryServiceType === type) {
        // gasimo baš primarnu → uzmi prvu preostalu
        const still = Object.keys(next).filter((t) => next[t]);
        nextPrimary = still[0] || '';
      }

      setPrimaryServiceType(nextPrimary);
      if (nextPrimary) {
        syncProgramTypeFromPrimary(nextPrimary);
      }

      return next;
    });
    setErrors((prev) => ({ ...prev, disciplines: undefined }));
  };

  const setPrimaryFromButton = (type) => {
    if (!disciplineSelection[type]) return;
    setPrimaryServiceType(type);
    syncProgramTypeFromPrimary(type);
    setErrors((prev) => ({ ...prev, disciplines: undefined }));
  };

  // Load student taxonomy (edit)
  useEffect(() => {
    if (student?.id) {
      API.get(`/students/${student.id}/taxonomy`)
        .then((res) => setNeeds({ needs: res.data?.needs || [] }))
        .catch(() => setNeeds({ needs: [] }));
    } else {
      setNeeds({ needs: [] });
    }
    setNeedsDirty(false);
  }, [student]);

  // Load teachers + their taxonomy
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get('/teachers/planner');
        const base = (res.data || []).filter((t) => t.is_active);
        if (cancelled) return;
        setTeachers(base);

        const entries = await Promise.all(
          base.map(async (t) => {
            try {
              const tx = await API.get(`/teachers/${t.id}/taxonomy`);
              return [t.id, tx.data || { strengths: [], limitations: [] }];
            } catch {
              return [t.id, { strengths: [], limitations: [] }];
            }
          })
        );
        if (cancelled) return;
        setTeacherTaxoMap(new Map(entries));
        setTaxoLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setTeachers([]);
        setTeacherTaxoMap(new Map());
        setTaxoLoaded(true);
        toast.error('Error loading providers');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Populate student for edit / add (bez auto-EI za novog dok ne znamo dostupne discipline)
  useEffect(() => {
    if (student) {
      setStudentData({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        dob: student.dob?.slice(0, 10) || '',
        gender: student.gender || '',
        is_active: student.is_active ?? true,
        default_teacher_id: student.default_teacher_id || '',
        latitude: student.latitude || '',
        longitude: student.longitude || '',
        notes: student.notes || '',
        uci: student.uci || '',
        program_type: student.program_type || 'EI',
        address_street: student.address_street || '',
        address_city: student.address_city || '',
        address_state: student.address_state || '',
        address_zip: student.address_zip || '',
        address_country: student.address_country || '',
        alt_address_street: student.alt_address_street || '',
        alt_address_city: student.alt_address_city || '',
        alt_address_state: student.alt_address_state || '',
        alt_address_zip: student.alt_address_zip || '',
        alt_address_country: student.alt_address_country || '',
        service_start_date: student.service_start_date?.slice(0, 10) || today,
        service_end_date: student.service_end_date?.slice(0, 10) || today,
        intake_goals: student.intake_goals || '',
        additional_notes: student.additional_notes || '',
        parent_concerns: student.parent_concerns || '',
        referring_agent: student.referring_agent || '',
        referring_agent_phone: student.referring_agent_phone || '',
        referring_agent_unit: student.referring_agent_unit || '',
        referring_agent_unit_id: student.referring_agent_unit_id || '',
        onboarding_documents: student.onboarding_documents || 'not signed',
        provider_gender_preference:
          student.provider_gender_preference || 'any',
        telehealth_ok: !!student.telehealth_ok,
        parents: student.parents?.length
          ? student.parents.map((p) => ({
              id: p.id || undefined,
              first_name: p.first_name || '',
              last_name: p.last_name || '',
              phone: p.phone || '',
              email: p.email || '',
              relation: p.relation || 'guardian',
            }))
          : [{ ...emptyParent }],
      });

      // Provider history
      API.get(`/students/${student.id}/teacher-history`)
        .then((res) => setTeacherHistory(res.data || []))
        .catch(() => setTeacherHistory([]));

      // Disciplines iz BE (students/all -> student.disciplines)
      const ds = Array.isArray(student.disciplines) ? student.disciplines : [];
      if (ds.length > 0) {
        const nextSel = { ...initialDisciplineSelection };
        let primary = '';
        ds.forEach((d) => {
          const st = (d.service_type || '').toUpperCase();
          if (nextSel[st] !== undefined) {
            nextSel[st] = true;
          }
          if (d.is_primary && !primary) {
            primary = st;
          }
        });
        if (!primary) {
          const first = ds[0]?.service_type?.toUpperCase();
          if (first && nextSel[first] !== undefined) primary = first;
        }
        setDisciplineSelection(nextSel);
        setPrimaryServiceType(primary || 'EI');
        syncProgramTypeFromPrimary(primary || 'EI');
      } else {
        // fallback iz program_type (EI/PI)
        const st = student.program_type === 'PI' ? 'PI' : 'EI';
        const nextSel = { ...initialDisciplineSelection };
        if (nextSel[st] !== undefined) {
          nextSel[st] = true;
        }
        setDisciplineSelection(nextSel);
        setPrimaryServiceType(st);
        syncProgramTypeFromPrimary(st);
      }
    } else {
      // Novi student: reset, ali ne biramo odmah EI dok ne znamo šta je dostupno
      setTeacherHistory([]);
      setDisciplineSelection({ ...initialDisciplineSelection });
      setPrimaryServiceType('');
    }
    setNeedsDirty(false);
  }, [student]);

  // Za NOVOG studenta: kad znamo koje discipline postoje → automatski izaberi prvu dostupnu
  useEffect(() => {
    if (!disciplinesLoaded) return;
    if (student) return; // u edit modu poštujemo ono što već postoji

    setDisciplineSelection((prev) => {
      const alreadySelected = Object.values(prev).some(Boolean);
      if (alreadySelected) return prev;

      // prvo po prioritetu (EI, PI, ABA, OT, PT), pa sve ostale (ako nekad dodaš novu u STATIC_DISCIPLINE_LABELS)
      const ordered = [
        ...DISCIPLINE_PRIORITY_ORDER,
        ...ALL_DISCIPLINE_CODES.filter(
          (c) => !DISCIPLINE_PRIORITY_ORDER.includes(c)
        ),
      ];
      const first = ordered.find((code) => availableServiceTypes.has(code));
      if (!first) return prev;

      const next = { ...initialDisciplineSelection, [first]: true };
      setPrimaryServiceType(first);
      syncProgramTypeFromPrimary(first);
      return next;
    });
  }, [disciplinesLoaded, availableServiceTypes, student]);

  // Provera da li je učitelj kvalifikovan za konkretnu disciplinu (EI/PI/ABA/OT/PT)
  const isTeacherQualifiedForDiscipline = (teacherId, disciplineCode) => {
    if (!disciplineCode) return false;
    const tx = teacherTaxoMap.get(Number(teacherId));
    if (!tx) return false;

    // PROG_* kod iz taxonomy, sa naming fallback-om
    const progCodeFromTax = programCodeMap[disciplineCode];
    const progCode =
      progCodeFromTax || `PROG_${disciplineCode.toUpperCase()}`;

    if (!progCode) return false;
    return teacherHasProgram(tx, progCode);
  };

  // Reset učitelja ako nije kvalifikovan za primarnu disciplinu
  useEffect(() => {
    if (!taxoLoaded) return;
    if (!primaryServiceType) return;
    const tid = studentData.default_teacher_id;
    if (!tid) return;
    const ok = isTeacherQualifiedForDiscipline(tid, primaryServiceType);
    if (!ok) {
      setStudentData((prev) => ({ ...prev, default_teacher_id: '' }));
      toast.info(
        'Selected provider is not qualified for the primary discipline. Please select another provider.'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryServiceType, taxoLoaded, programCodeMap]);

  // Lista kvalifikovanih učitelja prema primarnoj disciplini
  const qualifiedTeachers = useMemo(() => {
    if (!taxoLoaded) return [];
    const disc = primaryServiceType || 'EI';
    return teachers.filter((t) => isTeacherQualifiedForDiscipline(t.id, disc));
  }, [teachers, teacherTaxoMap, primaryServiceType, taxoLoaded, programCodeMap]);

  // Ako smo u EDIT-u i current teacher nije kvalifikovan, i dalje ga prikaži na vrhu
  const teacherOptions = useMemo(() => {
    const opts = [...qualifiedTeachers];
    const currentId = Number(studentData.default_teacher_id);
    if (taxoLoaded && currentId && !opts.some((t) => t.id === currentId)) {
      const found = teachers.find((t) => t.id === currentId);
      if (found) opts.unshift(found);
    }
    return opts;
  }, [qualifiedTeachers, teachers, studentData.default_teacher_id, taxoLoaded]);

  const validate = () => {
    let errs = {};
    requiredFields.forEach((f) => {
      if (
        !studentData[f] ||
        (typeof studentData[f] === 'string' && !studentData[f].trim())
      ) {
        errs[f] = 'Required';
      }
    });

    // Disciplines – bar jedna izabrana
    const selectedTypes = Object.keys(disciplineSelection).filter(
      (t) => disciplineSelection[t]
    );
    if (selectedTypes.length === 0) {
      errs.disciplines =
        'Select at least one discipline configured for this organization. If you see all options disabled, please contact your administrator.';
    }

    // Validacija providera – samo ako je taksonomija učitana
    if (taxoLoaded && studentData.default_teacher_id) {
      if (!primaryServiceType) {
        errs.default_teacher_id = 'Select a primary discipline first.';
      } else {
        const ok = isTeacherQualifiedForDiscipline(
          studentData.default_teacher_id,
          primaryServiceType
        );
        if (!ok) {
          errs.default_teacher_id =
            'Selected provider is not qualified for the primary discipline.';
        }
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStudentChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'provider_gender_preference' || name === 'telehealth_ok') {
      setNeedsDirty(true);
    }
    setStudentData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'default_teacher_id'
          ? value === ''
            ? null
            : parseInt(value)
          : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleParentChange = (index, e) => {
    const { name, value } = e.target;
    setStudentData((prev) => {
      const updated = [...prev.parents];
      updated[index] = { ...updated[index], [name]: value };
      return { ...prev, parents: updated };
    });
  };

  const addParent = () =>
    setStudentData((prev) => ({
      ...prev,
      parents: [...prev.parents, { ...emptyParent, relation: 'guardian' }],
    }));
  const removeParent = (index) =>
    setStudentData((prev) => ({
      ...prev,
      parents: prev.parents.filter((_, i) => i !== index),
    }));

  const saveAll = async (stayOpen = false) => {
    if (!validate()) return;
    setLoading(true);
    try {
      // Discipline payload
      const selectedTypes = Object.keys(disciplineSelection).filter(
        (t) => disciplineSelection[t]
      );
      let primary = primaryServiceType;
      if (!primary && selectedTypes.length > 0) {
        primary = selectedTypes[0];
        setPrimaryServiceType(primary);
        syncProgramTypeFromPrimary(primary);
      }

      const disciplinesPayload = {
        selected: selectedTypes,
        primary: primary || null,
      };

      // program_type (EI/PI) izveden iz primarne discipline
      const legacyProgramType = primary === 'PI' ? 'PI' : 'EI';

      const payload = {
        ...studentData,
        default_teacher_id: isNaN(studentData.default_teacher_id)
          ? null
          : Number(studentData.default_teacher_id),
        latitude: studentData.latitude || null,
        longitude: studentData.longitude || null,
        address_country: studentData.address_country || '',
        service_start_date: studentData.service_start_date || today,
        service_end_date: studentData.service_end_date || today,
        program_type: legacyProgramType,
        disciplines: disciplinesPayload,
      };

      let studentId;
      const isNew = !student?.id;

      if (!isNew) {
        const res = await API.put(`/students/${student.id}`, payload);
        studentId = student.id;
        toast.success('Student updated!');
        if (res.data?.teacherChanged) {
          toast.info(
            'Be sure to update scheduled visits for this student, as all existing visits have been automatically marked as rejected.'
          );
        }
        API.get(`/students/${student.id}/teacher-history`).then((res) =>
          setTeacherHistory(res.data || [])
        );
      } else {
        const res = await API.post('/students', payload);
        studentId = res.data?.student_id || res.data?.id;
        toast.success('Student saved!');
        setTimeout(() => {
          toast.info(
            'You can now set Needs & Preferences and view Suggested Providers from the Edit Student screen.',
            { autoClose: 6000 }
          );
        }, 300);
      }

      if (studentId) {
        const needsPayload = (needs?.needs || []).map((x) =>
          typeof x === 'string'
            ? { code: x, priority: 1, required: false }
            : {
                code: x.code,
                priority: x.priority || 1,
                required: !!x.required,
              }
        );
        try {
          await saveStudentTaxonomy(studentId, { needs: needsPayload });
        } catch (_) {}
      }

      if (stayOpen) {
        toast.info(
          'Tip: Suggested Providers may change with your updates. Use Refresh in that panel if needed.'
        );
        setNeedsDirty(false);
        return;
      }

      onSaved && onSaved();

      if (!student?.id) {
        // reset forme posle ADD
        setStudentData({
          first_name: '',
          last_name: '',
          dob: '',
          gender: '',
          is_active: true,
          default_teacher_id: '',
          latitude: '',
          longitude: '',
          notes: '',
          uci: '',
          program_type: 'EI',
          address_street: '',
          address_city: '',
          address_state: '',
          address_zip: '',
          address_country: '',
          alt_address_street: '',
          alt_address_city: '',
          alt_address_state: '',
          alt_address_zip: '',
          alt_address_country: '',
          service_start_date: today,
          service_end_date: today,
          intake_goals: '',
          additional_notes: '',
          parent_concerns: '',
          referring_agent: '',
          referring_agent_phone: '',
          referring_agent_unit: '',
          referring_agent_unit_id: '',
          onboarding_documents: 'not signed',
          provider_gender_preference: 'any',
          telehealth_ok: false,
          parents: [{ ...emptyParent }],
        });
        setDisciplineSelection({ ...initialDisciplineSelection });
        setPrimaryServiceType('');
        setNeeds({ needs: [] });
        setNeedsDirty(false);
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message;
      if (
        msg === 'UCI must be unique' ||
        msg === 'UCI must be unique within organization'
      ) {
        toast.error('UCI must be unique. Another student already has this UCI.');
        setErrors((prev) => ({ ...prev, uci: 'Duplicate UCI' }));
      } else if (
        err.response?.data?.error === 'Address not found. Please check.'
      ) {
        toast.error(
          'Address not found! Please check all address fields and try again.'
        );
        setErrors((prev) => ({
          ...prev,
          address_street: 'Invalid',
          address_city: 'Invalid',
          address_state: 'Invalid',
          address_zip: 'Invalid',
          address_country: 'Invalid',
        }));
      } else if (err.response?.data?.missing) {
        let errs = {};
        for (let f of err.response.data.missing) errs[f] = 'Required';
        setErrors(errs);
        toast.error('Please fill all required fields.');
      } else {
        toast.error(msg || 'Failed to save student');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveAll(false);
  };

  const renderFieldError = (field) =>
    errors[field] && (
      <span className="text-red-500 text-xs ml-1">{errors[field]}</span>
    );

  const currentTeacherNotQualified = (() => {
    if (!taxoLoaded) return false;
    if (!primaryServiceType) return false;
    const tid = Number(studentData.default_teacher_id);
    if (!tid) return false;
    return !isTeacherQualifiedForDiscipline(tid, primaryServiceType);
  })();

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 bg-white p-4 rounded shadow max-w-2xl mx-auto"
    >
      <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
        {student ? <FaEdit /> : <FaUserPlus />}{' '}
        {student ? 'Edit' : 'Add'} Student
      </h2>

      {/* Basic */}
      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            First Name *
          </label>
          <input
            name="first_name"
            value={studentData.first_name}
            onChange={handleStudentChange}
            placeholder="First Name *"
            className={`w-full border p-2 rounded ${
              errors.first_name ? 'border-red-500' : ''
            }`}
          />
          {renderFieldError('first_name')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Last Name *
          </label>
          <input
            name="last_name"
            value={studentData.last_name}
            onChange={handleStudentChange}
            placeholder="Last Name *"
            className={`w-full border p-2 rounded ${
              errors.last_name ? 'border-red-500' : ''
            }`}
          />
          {renderFieldError('last_name')}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Date of Birth *
          </label>
          <input
            type="date"
            name="dob"
            value={studentData.dob}
            onChange={handleStudentChange}
            className={`w-full border p-2 rounded ${
              errors.dob ? 'border-red-500' : ''
            }`}
          />
          {renderFieldError('dob')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Gender *
          </label>
          <select
            name="gender"
            value={studentData.gender}
            onChange={handleStudentChange}
            className={`w-full border p-2 rounded ${
              errors.gender ? 'border-red-500' : ''
            }`}
          >
            <option value="">Gender *</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {renderFieldError('gender')}
        </div>
        <div className="flex items-center mt-6 md:mt-6">
          <input
            type="checkbox"
            name="is_active"
            checked={studentData.is_active}
            onChange={handleStudentChange}
            className="mr-2"
          />
          <label>Active</label>
        </div>
      </div>

      {/* DISCIPLINES */}
      <div className="mt-2 border rounded-lg p-3 bg-gray-50">
        <label className="text-sm font-semibold mb-1 block">
          Disciplines (service types) *
        </label>
        <p className="text-xs text-gray-600 mb-2">
          Select one or more disciplines for this child. One of them should be
          marked as primary.
        </p>
        {disciplinesLoaded && availableServiceTypes.size === 0 && (
          <p className="text-xs text-red-600 mb-2">
            No disciplines are currently configured for this organization (or as
            a global template). Please contact your administrator.
          </p>
        )}
        {!disciplinesLoaded && (
          <p className="text-xs text-gray-500 mb-2">
            Loading discipline configuration...
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {ALL_DISCIPLINE_CODES.map((type) => {
            const isSelected = disciplineSelection[type];
            const isPrimary = primaryServiceType === type;
            const isAllowed = availableServiceTypes.has(type);

            const baseClass = isSelected
              ? 'bg-blue-100 border-blue-500 text-blue-800'
              : 'bg-gray-100 border-gray-300 text-gray-700';

            const disabledClass = !isAllowed
              ? 'opacity-40 cursor-not-allowed'
              : 'cursor-pointer';

            const label =
              programLabelMap[type] || STATIC_DISCIPLINE_LABELS[type] || type;

            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  if (isAllowed) toggleServiceType(type);
                }}
                disabled={!isAllowed}
                className={`px-3 py-1 rounded-full text-xs border flex items-center gap-2 ${baseClass} ${disabledClass}`}
                title={
                  isAllowed
                    ? undefined
                    : 'This discipline is not configured for this organization (or globally) and cannot be selected.'
                }
              >
                <span className="font-semibold">{type}</span>
                <span className="hidden sm:inline">{label}</span>

                {isSelected && (
                  <button
                    type="button"
                    disabled={!isAllowed}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAllowed) return;
                      setPrimaryFromButton(type);
                    }}
                    className={[
                      'ml-1 px-2 py-0.5 rounded-full text-[10px] border',
                      !isAllowed
                        ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed'
                        : isPrimary
                        ? 'bg-yellow-400 text-black border-yellow-500'
                        : 'bg-white text-gray-700 border-gray-300',
                    ].join(' ')}
                    title={
                      !isAllowed
                        ? 'This discipline is not configured for this organization (or globally).'
                        : 'Set as primary discipline'
                    }
                  >
                    {isPrimary ? 'Primary' : 'Make primary'}
                  </button>
                )}
              </button>
            );
          })}
        </div>
        {errors.disciplines && (
          <div className="text-red-500 text-xs mt-1">{errors.disciplines}</div>
        )}
        <p className="mt-2 text-[11px] text-gray-500">
          The primary discipline will be used as the default service type for
          visits and skills (you can still override it per visit if needed).
        </p>
      </div>

      {/* Provider (filtered by primary discipline) */}
      <div className="mt-2">
        <label className="text-sm text-gray-700 mb-1 block">
          Default Provider *
        </label>
        <select
          name="default_teacher_id"
          value={studentData.default_teacher_id || ''}
          onChange={handleStudentChange}
          className={`w-full border p-2 rounded ${
            errors.default_teacher_id ? 'border-red-500' : ''
          }`}
          title="Select Provider (filtered by primary discipline)"
          disabled={!taxoLoaded}
        >
          <option value="">
            {taxoLoaded ? 'Select Provider *' : 'Loading providers...'}
          </option>

          {(() => {
            const curId = Number(studentData.default_teacher_id);
            const curTeacher = teachers.find((t) => t.id === curId);
            if (taxoLoaded && curTeacher && currentTeacherNotQualified) {
              return (
                <option key={`cur-${curId}`} value={curId}>
                  {curTeacher.name} — NOT qualified for{' '}
                  {primaryServiceType || 'selected discipline'}
                </option>
              );
            }
            return null;
          })()}

          {teacherOptions
            .filter(
              (t) =>
                !taxoLoaded ||
                !primaryServiceType ||
                isTeacherQualifiedForDiscipline(t.id, primaryServiceType)
            )
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
        </select>
        {renderFieldError('default_teacher_id')}
        {taxoLoaded && currentTeacherNotQualified && (
          <div className="text-xs text-red-600 mt-1">
            Selected provider is not qualified for the primary discipline —
            please re-select.
          </div>
        )}
      </div>

      {/* Provider history */}
      {isEditMode && (
        <div className="mt-2">
          <h4 className="font-semibold text-xs mb-1">Provider History</h4>
          <ul className="bg-gray-50 border rounded p-2 text-xs max-h-32 overflow-y-auto">
            {teacherHistory.length === 0 ? (
              <li>No provider changes recorded.</li>
            ) : (
              teacherHistory.map((item) => (
                <li key={item.id} className="mb-1">
                  <span className="font-semibold">
                    {item.old_teacher_name || 'Unknown'}
                  </span>{' '}
                  →{' '}
                  <span className="font-semibold">
                    {item.new_teacher_name || 'Unknown'}
                  </span>
                  <span className="ml-2 text-gray-500">
                    ({new Date(item.changed_at).toLocaleString()})
                  </span>
                  {item.admin_name && (
                    <span className="ml-2 text-blue-700">
                      by {item.admin_name}
                    </span>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Address */}
      <h3 className="font-semibold mt-2">Primary Address *</h3>
      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Street *</label>
          <input
            name="address_street"
            value={studentData.address_street}
            onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${
              errors.address_street ? 'border-red-500' : ''
            }`}
            placeholder="Street"
          />
          {renderFieldError('address_street')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">City *</label>
          <input
            name="address_city"
            value={studentData.address_city}
            onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${
              errors.address_city ? 'border-red-500' : ''
            }`}
            placeholder="City"
          />
          {renderFieldError('address_city')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">State *</label>
          <input
            name="address_state"
            value={studentData.address_state}
            onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${
              errors.address_state ? 'border-red-500' : ''
            }`}
            placeholder="State"
          />
          {renderFieldError('address_state')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">ZIP *</label>
          <input
            name="address_zip"
            value={studentData.address_zip}
            onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${
              errors.address_zip ? 'border-red-500' : ''
            }`}
            placeholder="ZIP"
          />
          {renderFieldError('address_zip')}
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Country *</label>
          <input
            name="address_country"
            value={studentData.address_country}
            onChange={handleStudentChange}
            className={`border p-2 rounded w-full ${
              errors.address_country ? 'border-red-500' : ''
            }`}
            placeholder="Country"
          />
          {renderFieldError('address_country')}
        </div>
      </div>

      {/* UCI */}
      <div>
        <label className="text-sm text-gray-700 mb-1 block">UCI *</label>
        <input
          name="uci"
          value={studentData.uci}
          onChange={handleStudentChange}
          className={`border p-2 rounded w-full ${
            errors.uci ? 'border-red-500' : ''
          }`}
          placeholder="UCI *"
        />
        {renderFieldError('uci')}
      </div>

      <div>
        <label className="text-sm text-gray-700 mb-1 block">Notes</label>
        <textarea
          name="notes"
          value={studentData.notes}
          onChange={handleStudentChange}
          className="border p-2 rounded w-full"
          placeholder="Notes"
        />
      </div>

      {/* Alternative Address */}
      <h3 className="font-semibold mt-2">Alternative Address (optional)</h3>
      <div className="grid md:grid-cols-3 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Street</label>
          <input
            name="alt_address_street"
            value={studentData.alt_address_street}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="Street"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">City</label>
          <input
            name="alt_address_city"
            value={studentData.alt_address_city}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="City"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">State</label>
          <input
            name="alt_address_state"
            value={studentData.alt_address_state}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="State"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">ZIP</label>
          <input
            name="alt_address_zip"
            value={studentData.alt_address_zip}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="ZIP"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Country</label>
          <input
            name="alt_address_country"
            value={studentData.alt_address_country}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="Country"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Start of Service Date
          </label>
          <input
            type="date"
            name="service_start_date"
            value={studentData.service_start_date}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="Start of Service Date"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            End of Service Date
          </label>
          <input
            type="date"
            name="service_end_date"
            value={studentData.service_end_date}
            onChange={handleStudentChange}
            className="border p-2 rounded w-full"
            placeholder="End of Service Date"
          />
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-700 mb-1 block">
          Intake Development Goals
        </label>
        <textarea
          name="intake_goals"
          value={studentData.intake_goals}
          onChange={handleStudentChange}
          className="border p-2 rounded w-full"
          placeholder="Intake Development Goals"
        />
      </div>
      <div>
        <label className="text-sm text-gray-700 mb-1 block">
          Additional Notes
        </label>
        <textarea
          name="additional_notes"
          value={studentData.additional_notes}
          onChange={handleStudentChange}
          className="border p-2 rounded w-full"
          placeholder="Additional Notes"
        />
      </div>
      <div>
        <label className="text-sm text-gray-700 mb-1 block">
          Parent Concerns
        </label>
        <textarea
          name="parent_concerns"
          value={studentData.parent_concerns}
          onChange={handleStudentChange}
          className="border p-2 rounded w-full"
          placeholder="Parent Concerns"
        />
      </div>

      {isEditMode ? (
        <>
          <h3 className="font-semibold mt-2">Needs & Preferences</h3>

          <div className="grid md:grid-cols-2 gap-2">
            <div>
              <label className="text-sm text-gray-700">
                Provider gender preference
              </label>
              <select
                name="provider_gender_preference"
                value={studentData.provider_gender_preference}
                onChange={handleStudentChange}
                className="w-full border p-2 rounded"
              >
                <option value="any">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <StudentTaxonomyEditor
            value={needs}
            onChange={(val) => {
              setNeeds(val);
              setNeedsDirty(true);
            }}
          />

          <div className="mt-3">
            <SuggestMatchesPanel studentId={student.id} />
          </div>
        </>
      ) : (
        <div className="mt-2 text-xs text-green-500">
          Save the student first to set Needs & Preferences and see Suggested
          Providers.
        </div>
      )}

      <h3 className="font-semibold mt-2">Referring Agent</h3>
      <div className="grid md:grid-cols-2 gap-2">
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Agent Name
          </label>
          <input
            name="referring_agent"
            value={studentData.referring_agent}
            onChange={handleStudentChange}
            className="border p-2 rounded"
            placeholder="Agent Name"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">
            Agent Phone
          </label>
          <input
            name="referring_agent_phone"
            value={studentData.referring_agent_phone}
            onChange={handleStudentChange}
            className="border p-2 rounded"
            placeholder="Agent Phone"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Unit</label>
          <input
            name="referring_agent_unit"
            value={studentData.referring_agent_unit}
            onChange={handleStudentChange}
            className="border p-2 rounded"
            placeholder="Unit"
          />
        </div>
        <div>
          <label className="text-sm text-gray-700 mb-1 block">Unit ID</label>
          <input
            name="referring_agent_unit_id"
            value={studentData.referring_agent_unit_id}
            onChange={handleStudentChange}
            className="border p-2 rounded"
            placeholder="Unit ID"
          />
        </div>
      </div>

      <label className="mt-2 block font-semibold">Onboarding Documents</label>
      <select
        name="onboarding_documents"
        value={studentData.onboarding_documents}
        onChange={handleStudentChange}
        className="border p-2 rounded w-full"
      >
        <option value="signed">Signed</option>
        <option value="not signed">Not Signed</option>
      </select>

      {/* Parents */}
      <div className="border-top pt-4">
        <h3 className="font-semibold mb-2">Parents</h3>
        {studentData.parents.map((parent, index) => (
          <div
            key={index}
            className="border p-3 mb-3 rounded bg-gray-50 space-y-2"
          >
            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="text-sm text-gray-700 mb-1 block">
                  First Name
                </label>
                <input
                  name="first_name"
                  value={parent.first_name}
                  onChange={(e) => handleParentChange(index, e)}
                  placeholder="First Name"
                  className="w-full border p-2 rounded"
                  required
                />
              </div>
              <div className="w-1/2">
                <label className="text-sm text-gray-700 mb-1 block">
                  Last Name
                </label>
                <input
                  name="last_name"
                  value={parent.last_name}
                  onChange={(e) => handleParentChange(index, e)}
                  placeholder="Last Name"
                  className="w-full border p-2 rounded"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-700 mb-1 block">Phone</label>
              <input
                name="phone"
                value={parent.phone}
                onChange={(e) => handleParentChange(index, e)}
                placeholder="Phone"
                className="w-full border p-2 rounded"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700 mb-1 block">Email</label>
              <input
                name="email"
                value={parent.email}
                onChange={(e) => handleParentChange(index, e)}
                placeholder="Email"
                className="w-full border p-2 rounded"
              />
            </div>

            <div>
              <label className="text-sm text-gray-700 mb-1 block">
                Relation
              </label>
              <select
                name="relation"
                value={parent.relation}
                onChange={(e) => handleParentChange(index, e)}
                className="w-full border p-2 rounded"
              >
                <option value="mother">Mother</option>
                <option value="father">Father</option>
                <option value="guardian">Guardian</option>
              </select>
            </div>

            {studentData.parents.length > 1 && (
              <button
                type="button"
                onClick={() => removeParent(index)}
                className="text-red-500 underline text-sm"
              >
                <FaTimes className="inline mr-1" /> Remove
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addParent}
          className="text-blue-600 underline text-sm"
        >
          <FaUserPlus className="inline mr-1" /> Add Another Parent
        </button>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2"
          >
            <FaTimes /> Cancel
          </button>
        )}

        {isEditMode && needsDirty ? (
          <button
            type="button"
            onClick={() => saveAll(true)}
            disabled={loading}
            className="bg-orange-600 text-white px-4 py-2 rounded flex items-center gap-2"
            title="Save updates but stay on this screen"
          >
            <FaSave /> {loading ? 'Updating...' : 'Update Student'}
          </button>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <FaSave /> {loading ? 'Saving...' : 'Save Student'}
          </button>
        )}
      </div>
    </form>
  );
};

export default AddStudent;












